/**
 * Login flow: email/phone OTP, {@link AuthProvider} wait, React Query cache reset.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationResult, signOut } from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import {
  loginWithEmail,
  sendPhoneOtp,
  verifyPhoneOtp,
  sendPasswordResetEmail,
} from '@/services/auth.service';
import { auth, initRecaptchaVerifier, setAuthPersistence } from '@/config/firebase';
import {
  getErrorMessage,
  consumeTutorBlockedSessionMessage,
  consumePendingActivationSessionMessage,
  isPendingActivationError,
  isTutorBlockedError,
} from '@/utils/errors-utils';
import { detectIdentifierType, formatPhoneNumber } from '@/utils/auth-validation-utils';
import type { IdentifierType } from '@/utils/auth-validation-utils';
import { toast } from '@/components/ui/toast';

/** Login identifier step: unset, email+password, or phone OTP. */
export type LoginMode = 'initial' | 'email' | 'phone';

/** Return type of {@link useLogin}. */
interface UseLoginReturn {
  mode: LoginMode;
  identifier: string;
  password: string;
  rememberMe: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  error: string | undefined;

  setIdentifier: (_value: string) => void;
  setPassword: (_value: string) => void;
  setRememberMe: (_value: boolean) => void;

  handleIdentifierChange: (_value: string) => void;
  switchToPhone: (_phoneNumber: string) => void;
  handleEmailLogin: () => Promise<void>;
  handleSendOtp: () => Promise<ConfirmationResult>;
  handleVerifyOtp: (_confirmationResult: ConfirmationResult, _code: string) => Promise<void>;
  handleResetPassword: (_email: string) => Promise<void>;

  clearError: () => void;
}

/** Catalog code thrown when bootstrap finds a tutor blocked from the web app. */
const TUTOR_BLOCKED_BOOTSTRAP_ERROR = 'tutorAppAccessDenied';
/** Catalog code thrown when bootstrap finds an account pending activation. */
const PENDING_ACTIVATION_BOOTSTRAP_ERROR = 'accountPendingActivation';
/** `GET /users/login` can exceed 10s on a loaded local Nest — do not sign out mid-bootstrap. */
const AUTH_BOOTSTRAP_TIMEOUT_MS = 45_000;

/**
 * Waits for {@link AuthProvider} to finish bootstrapping auth ({@link fetchUserData}, preferences,
 * interface elements). Resolves as soon as isAuthenticated becomes true in the store.
 * Rejects early when api-client flags tutor 403 (sessionStorage) so login can show API message.
 */
const waitForAuthBootstrap = (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    if (useAuthStore.getState().isAuthenticated) {
      resolve();
      return;
    }

    // Synchronous check — AuthProvider may have set the flag before the poll starts.
    if (sessionStorage.getItem('tutor_blocked') === 'true') {
      reject(new Error(getErrorMessage(TUTOR_BLOCKED_BOOTSTRAP_ERROR)));
      return;
    }
    if (sessionStorage.getItem('pending_activation') === 'true') {
      reject(new Error(getErrorMessage(PENDING_ACTIVATION_BOOTSTRAP_ERROR)));
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timeout);
      clearInterval(poll);
      unsub();
    };

    const poll = setInterval(() => {
      if (sessionStorage.getItem('tutor_blocked') === 'true') {
        finish();
        reject(new Error(getErrorMessage(TUTOR_BLOCKED_BOOTSTRAP_ERROR)));
      }
      if (sessionStorage.getItem('pending_activation') === 'true') {
        finish();
        reject(new Error(getErrorMessage(PENDING_ACTIVATION_BOOTSTRAP_ERROR)));
      }
    }, 100);

    const timeout = setTimeout(() => {
      finish();
      reject(new Error(getErrorMessage('authBootstrapTimeout')));
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);

    const unsub = useAuthStore.subscribe((state) => {
      if (state.isAuthenticated) {
        finish();
        resolve();
      }
    });
  });
};

/** Resolve the tutor blocked toast message. */
function resolveTutorBlockedToastMessage(err: unknown): string | null {
  const fromSession = consumeTutorBlockedSessionMessage();
  if (fromSession !== null) return getErrorMessage(fromSession);
  if (err instanceof Error && err.message === TUTOR_BLOCKED_BOOTSTRAP_ERROR) {
    return getErrorMessage('tutorAppAccessDenied');
  }
  if (isTutorBlockedError(err)) return getErrorMessage(err);
  return null;
}

/** Sign out safely. */
async function signOutSafely(): Promise<void> {
  try {
    await signOut(auth);
  } catch {
    /* Firebase may already be signed out */
  }
}

/** Apply the login failure. */
async function applyLoginFailure(err: unknown, setError: (message: string) => void): Promise<void> {
  const pendingMessage = resolvePendingActivationToastMessage(err);
  if (pendingMessage !== null) {
    await signOutSafely();
    setError(pendingMessage);
    return;
  }

  const tutorMessage = resolveTutorBlockedToastMessage(err);
  if (tutorMessage !== null) {
    await signOutSafely();
    setError(tutorMessage);
    toast.error(getErrorMessage(tutorMessage));
    return;
  }

  const errorMessage = getErrorMessage(err);
  setError(errorMessage);
  toast.error(errorMessage);
  await signOutSafely();
}

/** Resolve the pending activation toast message. */
function resolvePendingActivationToastMessage(err: unknown): string | null {
  const fromSession = consumePendingActivationSessionMessage();
  if (fromSession !== null) return getErrorMessage(fromSession);
  if (err instanceof Error && err.message === PENDING_ACTIVATION_BOOTSTRAP_ERROR) {
    return getErrorMessage('accountPendingActivation');
  }
  if (isPendingActivationError(err)) return getErrorMessage(err);
  return null;
}

/** Email/phone login; waits for {@link AuthProvider} then clears the React Query cache. */
export const useLogin = (): UseLoginReturn => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<LoginMode>('initial');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /** Clear the error. */
  const clearError = () => setError(undefined);

  /** Switch straight to phone mode, skipping {@link detectIdentifierType}. */
  const switchToPhone = (phoneNumber: string) => {
    setIdentifier(phoneNumber);
    setError(undefined);
    setMode('phone');
  };

  /** Handle identifier change. */
  const handleIdentifierChange = (value: string) => {
    setIdentifier(value);
    setError(undefined);

    const type: IdentifierType = detectIdentifierType(value);

    if (type === 'email') {
      setMode('email');
    } else if (type === 'phone') {
      setMode('phone');
    } else {
      setMode('initial');
    }
  };

  /** Handle email login. */
  const handleEmailLogin = async () => {
    setIsLoading(true);
    setError(undefined);
    setIsSuccess(false);

    try {
      await setAuthPersistence(rememberMe);

      await loginWithEmail(identifier, password);
      // No getIdToken(true) here — token is brand new after loginWithEmail,
      // and AuthProvider.onAuthStateChanged will call getIdToken(true) once.

      // AuthProvider.onAuthStateChanged handles ALL data loading (fetchUserData,
      // getUserPreferences, getInterfaceElements). We wait for it to finish.
      await waitForAuthBootstrap();

      // Clear React Query cache to force fresh data with new role permissions
      queryClient.clear();

      setIsSuccess(true);
      await new Promise((resolve) => setTimeout(resolve, 800));
      void navigate('/dashboard');
    } catch (err) {
      await applyLoginFailure(err, setError);
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  /** Handle send OTP. */
  const handleSendOtp = async (): Promise<ConfirmationResult> => {
    setIsLoading(true);
    setError(undefined);

    try {
      await setAuthPersistence(rememberMe);

      const formattedPhone = formatPhoneNumber(identifier);

      const recaptchaVerifier = initRecaptchaVerifier();

      const confirmationResult = await sendPhoneOtp(formattedPhone, recaptchaVerifier);
      toast.success('Code OTP envoyé');

      return confirmationResult;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /** Handle verify OTP. */
  const handleVerifyOtp = async (
    confirmationResult: ConfirmationResult,
    code: string,
  ): Promise<void> => {
    setIsLoading(true);
    setError(undefined);
    setIsSuccess(false);

    try {
      await verifyPhoneOtp(confirmationResult, code);

      // AuthProvider.onAuthStateChanged handles ALL data loading (fetchUserData,
      // getUserPreferences, getInterfaceElements). We wait for it to finish.
      await waitForAuthBootstrap();

      // Clear React Query cache to force fresh data with new role permissions
      queryClient.clear();

      setIsSuccess(true);
      await new Promise((resolve) => setTimeout(resolve, 800));
      void navigate('/dashboard');
    } catch (err) {
      await applyLoginFailure(err, setError);
      setIsSuccess(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /** Handle reset password. */
  const handleResetPassword = async (email: string): Promise<void> => {
    setIsLoading(true);
    setError(undefined);

    try {
      await sendPasswordResetEmail(email);
      toast.success(
        'Si un compte est associé à cette adresse, un email vous a été envoyé. Si vous venez d’être invité, ouvrez le bouton « Activer mon compte ».',
      );
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mode,
    identifier,
    password,
    rememberMe,
    isLoading,
    isSuccess,
    error,

    setIdentifier,
    setPassword,
    setRememberMe,

    handleIdentifierChange,
    switchToPhone,
    handleEmailLogin,
    handleSendOtp,
    handleVerifyOtp,
    handleResetPassword,

    clearError,
  };
};
